//#region ../src/utils/qr-payload/qr-code-origin.ts
/**
* Is this decoded QR text one of OUR launch URLs?
*
* WHY THIS EXISTS — it is a safety boundary, not a convenience. Decoding a QR
* turns a printed sticker into a URL, and the consumer path then wants to
* fetch that URL: an app that scans whatever the camera sees would let a
* stranger's sticker start an outbound request from the AR frame path, at an
* address of their choosing, with no user action beyond looking at it. The
* same predicate also gates MINTING, because an app that anchors every code
* it sees would write a real latitude and longitude for a shop's WiFi code
* into a zip the author then publishes.
*
* It fails CLOSED: anything it cannot positively recognise is not ours.
*
* SEE `GpsPlusSlamJs_Docs/docs/2026-08-28-0636-recorder-qr-anchor-authoring-plan.md`
* §3 M-A and §6 (the review finding that produced it).
*/
/** The query parameter every launch URL carries. */
const PAYLOAD_PARAM = "qr";
/** Only these schemes can name something we would fetch. */
const ALLOWED_PROTOCOLS = new Set(["http:", "https:"]);
/**
* @param text the decoded QR payload
* @param allowedHosts exact host names we own, e.g. `['gps.csutil.com']`.
*   Compared case-insensitively against the parsed hostname — never as a
*   prefix or suffix. An empty list means nothing is ours.
* @returns whether `text` is one of our launch URLs and carries a payload
*/
function qrCodeIsOurs(text, allowedHosts) {
	if (typeof text !== "string" || text === "") return false;
	if (!Array.isArray(allowedHosts) || allowedHosts.length === 0) return false;
	let url;
	try {
		url = new URL(text);
	} catch {
		return false;
	}
	if (!ALLOWED_PROTOCOLS.has(url.protocol)) return false;
	const host = stripTrailingDot(url.hostname);
	if (!allowedHosts.some((allowed) => typeof allowed === "string" && stripTrailingDot(allowed.toLowerCase()) === host)) return false;
	const payload = url.searchParams.get(PAYLOAD_PARAM);
	return payload !== null && payload.trim() !== "";
}
/** `example.com.` → `example.com`. One dot only; the root label is optional
*  in a URL but never part of the name anyone configures. */
function stripTrailingDot(host) {
	return host.endsWith(".") ? host.slice(0, -1) : host;
}
//#endregion
export { qrCodeIsOurs };
